import React, { useState, useEffect, useRef } from 'react';
import api from '../api/client';
import { normalizeGtin, isValidUpca } from '../utils/upca';
import { generateBarcodePDF } from '../utils/barcodeLabel';
import '../GetLabelsPage.css';

const Barcodes = () => {
  const [brands,        setBrands]        = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [products,      setProducts]      = useState([]);
  const [search,        setSearch]        = useState('');
  const [showDropdown,  setShowDropdown]  = useState(false);
  const [items,         setItems]         = useState([]);   // one row per product × GTIN
  const [labelW,        setLabelW]        = useState(80);
  const [labelH,        setLabelH]        = useState(50);
  const [showName,      setShowName]      = useState(true);
  const [files,         setFiles]         = useState([]);
  const [error,         setError]         = useState('');

  const searchRef   = useRef(null);
  const searchInput = useRef(null);

  useEffect(() => {
    api.get('/api/labels/brands')
      .then(r => setBrands(r.data.data))
      .catch(() => setError('Failed to load brands'));
  }, []);

  useEffect(() => {
    api.get('/api/labels/gtins' + (selectedBrand ? `?brandID=${selectedBrand}` : ''))
      .then(r => setProducts(r.data.data || []))
      .catch(() => setError('Failed to load products'));
  }, [selectedBrand]);

  useEffect(() => {
    const h = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const hasRow = (sku, gtin) => items.some((i) => i.sku === sku && i.gtin === gtin);
  const q = search.trim().toLowerCase();
  const filtered = products.filter(
    (p) =>
      !p.gtins.every((g) => hasRow(p.SKU, normalizeGtin(g.value))) &&
      (!q ||
        (p.name || '').toLowerCase().includes(q) ||
        p.SKU.toLowerCase().includes(q) ||
        p.gtins.some((g) => g.value.includes(q)))
  );

  // A product with several GTINs (main + alternate IDs) becomes one row per GTIN.
  const addProduct = (p) => {
    setItems((prev) => {
      const next = [...prev];
      for (const g of p.gtins) {
        const gtin = normalizeGtin(g.value);
        if (!next.some((i) => i.sku === p.SKU && i.gtin === gtin)) {
          next.push({ key: `${p.SKU}|${gtin}`, sku: p.SKU, name: p.name, gtin, kind: g.kind, valid: isValidUpca(gtin), qty: 1 });
        }
      }
      return next;
    });
    setSearch('');
    setShowDropdown(true);
    searchInput.current?.focus();
  };

  const updateQty  = (key, qty) => setItems((prev) => prev.map((i) => (i.key === key ? { ...i, qty } : i)));
  const removeItem = (key)      => setItems((prev) => prev.filter((i) => i.key !== key));

  const W = Number(labelW);
  const H = Number(labelH);
  const validItems  = items.filter((i) => i.valid);
  const canGenerate = validItems.length > 0 && W >= 20 && H >= 10;

  const handleGenerate = () => {
    setError('');
    try {
      setFiles(validItems.map((i) => ({
        name: `${i.name} ${i.gtin}`,
        sku:  i.sku,
        blob: generateBarcodePDF({ ...i, qty: Math.max(1, parseInt(i.qty) || 1), W, H, showName }).output('blob'),
      })));
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const downloadFile = (file) => {
    const url = URL.createObjectURL(file.blob);
    const a   = document.createElement('a');
    a.href = url; a.download = `${file.name}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  const downloadAll = () => files.forEach(downloadFile);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="gl-content">

      {error && <div className="gl-error">{error}</div>}

      {/* Label size */}
      <div className="gl-seed-qty-row">
        <div className="gl-qty-field">
          <label className="gl-label">Label width, mm</label>
          <input type="number" min="20" className="gl-input-lg" value={labelW}
            onChange={(e) => { setLabelW(e.target.value); setFiles([]); }} />
        </div>
        <div className="gl-qty-field">
          <label className="gl-label">Label height, mm</label>
          <input type="number" min="10" className="gl-input-lg" value={labelH}
            onChange={(e) => { setLabelH(e.target.value); setFiles([]); }} />
        </div>
        <label className="gl-label" style={{ marginBottom: 12, fontWeight: 400, cursor: 'pointer' }}>
          <input type="checkbox" checked={showName} style={{ marginRight: 6 }}
            onChange={(e) => { setShowName(e.target.checked); setFiles([]); }} />
          Product name on label
        </label>
      </div>

      {/* Brand filter */}
      <div className="gl-brand-wrap">
        <label className="gl-label">Brand (optional)</label>
        <select className="gl-select" value={selectedBrand}
          onChange={(e) => { setSelectedBrand(e.target.value); setSearch(''); }}>
          <option value="">— All brands —</option>
          {brands.map((b) => <option key={b.brandID} value={b.brandID}>{b.brandName}</option>)}
        </select>
      </div>

      {/* Product search */}
      <div className="gl-search-wrap" ref={searchRef}>
        <label className="gl-label">Add Products</label>
        <input
          ref={searchInput}
          type="text"
          className="gl-input"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
          placeholder="Search by name, SKU or GTIN — click to add, search again to add more…"
        />
        {showDropdown && (search || filtered.length > 0) && (
          <div className="gl-dropdown">
            {filtered.length === 0 ? (
              <div className="gl-dropdown-empty">{search ? 'No products with GTIN found' : 'Start typing to search…'}</div>
            ) : (
              filtered.slice(0, 100).map((p) => (
                <div key={`${p.source}|${p.SKU}`} className="gl-dropdown-item" onMouseDown={() => addProduct(p)}>
                  <span style={{ fontWeight: 500 }}>{p.name}</span>
                  <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>{p.SKU}</span>
                  {p.gtins.length > 1 && <span className="gl-source-badge">{p.gtins.length} GTIN</span>}
                  <span className="gl-source-badge">{p.source}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Selected rows */}
      {items.length > 0 && (
        <div className="gl-table-wrap">
          <table className="gl-table">
            <thead>
              <tr>
                <th className="gl-th">Product</th>
                <th className="gl-th gl-th-w120">SKU</th>
                <th className="gl-th gl-th-w120">GTIN</th>
                <th className="gl-th gl-th-w120 gl-th-center">Label Count</th>
                <th className="gl-th gl-th-w40 gl-th-center" />
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.key}>
                  <td className="gl-td">{i.name}</td>
                  <td className="gl-td gl-td-muted">{i.sku}</td>
                  <td className="gl-td" style={{ whiteSpace: 'nowrap' }}>
                    {i.gtin}
                    {i.kind === 'alt' && <span className="gl-source-badge">alt</span>}
                    {!i.valid && <span className="gl-source-badge" style={{ color: '#c62828' }}>invalid check digit</span>}
                  </td>
                  <td className="gl-td gl-td-center">
                    <input type="number" min="1" className="gl-num-input" value={i.qty}
                      onChange={(e) => updateQty(i.key, parseInt(e.target.value) || 1)} />
                  </td>
                  <td className="gl-td gl-td-center">
                    <button className="gl-remove-btn" onClick={() => removeItem(i.key)}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {items.length > 0 && files.length === 0 && (
        <button className="gl-btn gl-btn-primary gl-btn-generate" onClick={handleGenerate} disabled={!canGenerate}>
          Generate Barcodes
        </button>
      )}

      {files.length > 0 && (
        <div>
          <div className="gl-done-header">
            <span className="gl-done-label">Done! {files.length} file{files.length > 1 ? 's' : ''} ready</span>
            {files.length > 1 && <button className="gl-btn gl-btn-primary" onClick={downloadAll}>Download All</button>}
            <button className="gl-btn gl-btn-gray" onClick={() => setFiles([])}>New Batch</button>
          </div>
          <div className="gl-files-list">
            {files.map((f, idx) => (
              <div key={idx} className="gl-file-item">
                <div>
                  <span className="gl-file-name">{f.name}</span>
                  <span className="gl-file-sku">{f.sku}</span>
                </div>
                <button className="gl-btn gl-btn-primary gl-btn-sm" onClick={() => downloadFile(f)}>Download PDF</button>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default Barcodes;
